import { useCallback, useEffect, useRef, useState } from 'react';
import { cashCockpitApi } from '../services/cashCockpitApi';
import type {
    OverviewData, ByEntityData, ForecastData, FxExposureData, CounterpartyData,
    CreditLineEntry, IntercompanyLoan, DataQualityData, CockpitBankAccount,
    CounterpartyGroup, PlannedCashEvent,
} from '../types/cashCockpit';

export const ALL_COMPANIES = ['bin', 'dbi', 'dch', 'dit', 'dsp', 'dtr', 'dfr', 'dpt'];

const STORAGE_KEY = 'cashCockpit.filters';

export type CockpitTabId =
    | 'overview' | 'byEntity' | 'forecast' | 'fxExposure'
    | 'counterparty' | 'creditLines' | 'intercompany'
    | 'plannedEvents' | 'dataQuality' | 'settings';

interface PersistedFilters {
    entities: string[] | 'ALL';
    asOfDate: string;
    fxShock: number;
}

function loadPersisted(): PersistedFilters | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PersistedFilters;
        if (
            (parsed.entities === 'ALL' || Array.isArray(parsed.entities)) &&
            typeof parsed.asOfDate === 'string' &&
            typeof parsed.fxShock === 'number'
        ) {
            return parsed;
        }
        return null;
    } catch {
        return null;
    }
}

export function useCashCockpit() {
    const initial = loadPersisted();
    const [entities, setEntities] = useState<string[] | 'ALL'>(initial?.entities ?? 'ALL');
    const [asOfDate, setAsOfDate] = useState<string>(initial?.asOfDate ?? new Date().toISOString().slice(0, 10));
    const [fxShock, setFxShock] = useState<number>(initial?.fxShock ?? 5);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            window.sessionStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ entities, asOfDate, fxShock } as PersistedFilters)
            );
        } catch {
            // sessionStorage may be unavailable — ignore
        }
    }, [entities, asOfDate, fxShock]);

    // === Hero (always loaded) ===
    const [overview, setOverview] = useState<OverviewData | null>(null);
    const [forecast, setForecast] = useState<ForecastData | null>(null);

    // === Per-tab state ===
    const [byEntity, setByEntity] = useState<ByEntityData | null>(null);
    const [fxExposure, setFxExposure] = useState<FxExposureData | null>(null);
    const [counterparty, setCounterparty] = useState<CounterpartyData | null>(null);
    const [creditLines, setCreditLines] = useState<CreditLineEntry[]>([]);
    const [intercompany, setIntercompany] = useState<IntercompanyLoan[]>([]);
    const [dataQuality, setDataQuality] = useState<DataQualityData | null>(null);
    const [plannedEvents, setPlannedEvents] = useState<PlannedCashEvent[]>([]);
    const [bankAccounts, setBankAccounts] = useState<CockpitBankAccount[]>([]);
    const [counterpartyGroups, setCounterpartyGroups] = useState<CounterpartyGroup[]>([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Track which tabs have been requested so they re-fetch on filter change.
    // Hero data (overview + forecast) is always considered "requested".
    const requestedTabsRef = useRef<Set<CockpitTabId>>(new Set());

    // Filter signature — used to invalidate cached tabs on change.
    const filterSig = `${entities === 'ALL' ? 'ALL' : entities.join(',')}|${asOfDate}|${fxShock}`;

    // Per-tab last-loaded signature (for stale detection).
    const tabSigRef = useRef<Map<CockpitTabId, string>>(new Map());

    // === Hero loader (overview + forecast) ===
    const loadHero = useCallback(async () => {
        const entitiesArg = entities === 'ALL' ? undefined : entities;
        const [o, fc] = await Promise.allSettled([
            cashCockpitApi.overview(entitiesArg, asOfDate),
            cashCockpitApi.forecast(entitiesArg, 13, asOfDate),
        ]);
        if (o.status === 'fulfilled') setOverview(o.value);
        if (fc.status === 'fulfilled') setForecast(fc.value);
        const errs = [o, fc]
            .filter(r => r.status === 'rejected')
            .map(r => (r as PromiseRejectedResult).reason?.message ?? String((r as PromiseRejectedResult).reason));
        return errs;
    }, [entities, asOfDate]);

    // === Per-tab loaders ===
    const loadTabData = useCallback(async (tab: CockpitTabId): Promise<string[]> => {
        const entitiesArg = entities === 'ALL' ? undefined : entities;
        const errs: string[] = [];
        const safe = async <T,>(fn: () => Promise<T>, setter: (v: T) => void, label: string) => {
            try { setter(await fn()); } catch (e) {
                errs.push(`${label}: ${e instanceof Error ? e.message : String(e)}`);
            }
        };
        switch (tab) {
            case 'overview':
                // Already covered by loadHero — nothing extra
                break;
            case 'byEntity':
                await safe(() => cashCockpitApi.byEntity(entitiesArg, 12, asOfDate), setByEntity, 'byEntity');
                break;
            case 'forecast':
                // Forecast is part of Hero; ensure it's loaded (fxShock not used yet)
                break;
            case 'fxExposure':
                await safe(() => cashCockpitApi.fxExposure(entitiesArg, fxShock, asOfDate), setFxExposure, 'fxExposure');
                break;
            case 'counterparty':
                await safe(() => cashCockpitApi.counterparty(entitiesArg, asOfDate), setCounterparty, 'counterparty');
                break;
            case 'creditLines':
                await safe(() => cashCockpitApi.creditLines(entitiesArg, asOfDate), setCreditLines, 'creditLines');
                break;
            case 'intercompany':
                await safe(() => cashCockpitApi.intercompany(entitiesArg, asOfDate), setIntercompany, 'intercompany');
                break;
            case 'plannedEvents':
                await safe(async () => (await cashCockpitApi.plannedEventsReadOnly(entitiesArg)).events, setPlannedEvents, 'plannedEvents');
                break;
            case 'dataQuality':
                await safe(() => cashCockpitApi.dataQuality(), setDataQuality, 'dataQuality');
                break;
            case 'settings':
                await Promise.all([
                    safe(() => cashCockpitApi.bankAccounts(), setBankAccounts, 'bankAccounts'),
                    safe(() => cashCockpitApi.listGroups(), setCounterpartyGroups, 'counterpartyGroups'),
                ]);
                break;
        }
        tabSigRef.current.set(tab, filterSig);
        return errs;
    }, [entities, asOfDate, fxShock, filterSig]);

    /**
     * Public: fetch a tab's data on demand. Skips if already loaded for current filters.
     * Marks the tab as "requested" so it re-fetches on filter change.
     */
    const ensureTabLoaded = useCallback(async (tab: CockpitTabId) => {
        requestedTabsRef.current.add(tab);
        if (tabSigRef.current.get(tab) === filterSig) return; // cached for current filters
        setError(null);
        const errs = await loadTabData(tab);
        if (errs.length) setError(`Partial load: ${errs.slice(0, 3).join(' | ')}`);
    }, [filterSig, loadTabData]);

    /**
     * Filter-change effect: reload Hero + every previously requested tab in parallel.
     * Initial mount also lands here (Hero only).
     */
    useEffect(() => {
        let cancelled = false;
        const isInitial = overview == null && forecast == null;
        if (isInitial) setLoading(true);
        setError(null);

        (async () => {
            const heroErrs = await loadHero();
            if (cancelled) return;
            tabSigRef.current.set('overview', filterSig);
            tabSigRef.current.set('forecast', filterSig);

            // Re-load any other tabs the user already opened (skip overview/forecast — covered by Hero)
            const tabsToReload = Array.from(requestedTabsRef.current).filter(
                t => t !== 'overview' && t !== 'forecast'
            );
            const tabErrs = (await Promise.all(tabsToReload.map(t => loadTabData(t)))).flat();
            if (cancelled) return;

            const all = [...heroErrs, ...tabErrs];
            if (all.length) setError(`Partial load: ${all.slice(0, 3).join(' | ')}`);
            setLoading(false);
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterSig]);

    const reload = useCallback(async () => {
        // Invalidate cache for all requested tabs and Hero, then reload via filterSig effect
        tabSigRef.current.clear();
        const heroErrs = await loadHero();
        tabSigRef.current.set('overview', filterSig);
        tabSigRef.current.set('forecast', filterSig);

        const tabsToReload = Array.from(requestedTabsRef.current).filter(
            t => t !== 'overview' && t !== 'forecast'
        );
        const tabErrs = (await Promise.all(tabsToReload.map(t => loadTabData(t)))).flat();
        const all = [...heroErrs, ...tabErrs];
        setError(all.length ? `Partial load: ${all.slice(0, 3).join(' | ')}` : null);
    }, [filterSig, loadHero, loadTabData]);

    const reloadGroups = useCallback(async () => {
        try {
            const groups = await cashCockpitApi.listGroups();
            setCounterpartyGroups(groups);
            // Refresh counterparty view since it depends on groups
            const entitiesArg = entities === 'ALL' ? undefined : entities;
            const cp = await cashCockpitApi.counterparty(entitiesArg, asOfDate);
            setCounterparty(cp);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to reload groups');
        }
    }, [entities, asOfDate]);

    return {
        // filters
        entities, setEntities,
        asOfDate, setAsOfDate,
        fxShock, setFxShock,
        // data
        overview, byEntity, forecast, fxExposure, counterparty, creditLines,
        intercompany, dataQuality, plannedEvents, bankAccounts, counterpartyGroups,
        // state
        loading, error,
        // actions
        ensureTabLoaded,
        reload,
        reloadGroups,
    };
}
